import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';

interface EnflasyonOrani {
  enflasyonID: number;
  yil: number;
  ay: number;
  tufeOrani: number | null;
  ufeOrani: number | null;
  isAuto: boolean;
  guncellemeTarihi: string;
}

const AY_ADLARI = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const formatOran = (v: number | null) =>
  v == null ? null : `% ${v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatTarih = (s: string) =>
  new Date(s).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const EnflasyonOranlari: React.FC = () => {
  const [kayitlar, setKayitlar] = useState<EnflasyonOrani[]>([]);
  const [loading, setLoading] = useState(true);

  // Düzenleme modal state
  const [editTarget, setEditTarget] = useState<EnflasyonOrani | null>(null);
  const [editTufe, setEditTufe] = useState('');
  const [editUfe, setEditUfe] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Manuel ekleme modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addYil, setAddYil] = useState(String(new Date().getFullYear()));
  const [addAy, setAddAy] = useState(String(new Date().getMonth() + 1));
  const [addTufe, setAddTufe] = useState('');
  const [addUfe, setAddUfe] = useState('');
  const [addSaving, setAddSaving] = useState(false);

  const fetchKayitlar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/EnflasyonOranlari');
      setKayitlar(res.data);
    } catch {
      toast.error('Enflasyon oranları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKayitlar(); }, [fetchKayitlar]);

  const openEdit = (k: EnflasyonOrani) => {
    setEditTarget(k);
    setEditTufe(k.tufeOrani != null ? String(k.tufeOrani).replace('.', ',') : '');
    setEditUfe(k.ufeOrani != null ? String(k.ufeOrani).replace('.', ',') : '');
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    const tufe = editTufe ? parseFloat(editTufe.replace(',', '.')) : null;
    const ufe  = editUfe  ? parseFloat(editUfe.replace(',', '.'))  : null;
    setEditSaving(true);
    try {
      await axios.put(`/api/EnflasyonOranlari/${editTarget.enflasyonID}`, {
        tufeOrani: tufe,
        ufeOrani: ufe,
      });
      toast.success(`${AY_ADLARI[editTarget.ay]} ${editTarget.yil} verisi güncellendi`);
      setEditTarget(null);
      fetchKayitlar();
    } catch {
      toast.error('Güncelleme sırasında hata oluştu');
    } finally {
      setEditSaving(false);
    }
  };

  const handleAdd = async () => {
    const yil = Number(addYil);
    const ay  = Number(addAy);
    if (!yil || !ay) { toast.warning('Yıl ve ay zorunludur'); return; }
    const tufe = addTufe ? parseFloat(addTufe.replace(',', '.')) : null;
    const ufe  = addUfe  ? parseFloat(addUfe.replace(',', '.'))  : null;
    setAddSaving(true);
    try {
      await axios.post('/api/EnflasyonOranlari', { yil, ay, tufeOrani: tufe, ufeOrani: ufe });
      toast.success(`${AY_ADLARI[ay]} ${yil} verisi eklendi`);
      setShowAddModal(false);
      setAddTufe(''); setAddUfe('');
      fetchKayitlar();
    } catch (err: any) {
      const msg = err?.response?.data ?? 'Kayıt eklenemedi';
      toast.error(typeof msg === 'string' ? msg : 'Bu dönem için zaten kayıt mevcut');
    } finally {
      setAddSaving(false);
    }
  };

  const handleDelete = async (k: EnflasyonOrani) => {
    if (!window.confirm(`${AY_ADLARI[k.ay]} ${k.yil} kaydını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`/api/EnflasyonOranlari/${k.enflasyonID}`);
      toast.success('Kayıt silindi');
      fetchKayitlar();
    } catch {
      toast.error('Silme sırasında hata oluştu');
    }
  };

  // ─── Styles ───────────────────────────────────────────────────
  const card: React.CSSProperties = {
    background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
    overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  };
  const thStyle: React.CSSProperties = {
    padding: '11px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em',
    background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
  };
  const tdStyle: React.CSSProperties = {
    padding: '14px 16px', fontSize: 13, color: '#111827', verticalAlign: 'middle',
  };
  const rowHover: React.CSSProperties = {
    borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s',
  };
  const inp: React.CSSProperties = {
    width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
    borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4,
  };
  const btnOrange: React.CSSProperties = {
    padding: '7px 16px', background: '#cd6906', color: '#fff', border: 'none',
    borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  };
  const btnGhost: React.CSSProperties = {
    padding: '7px 14px', background: '#fff', color: '#374151',
    border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, cursor: 'pointer',
  };

  const guncelBadge = (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#dcfce7', color: '#16a34a' }}>
      Güncel
    </span>
  );
  const bekleyenBadge = (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#fee2e2', color: '#dc2626' }}>
      Veri Bekleniyor
    </span>
  );

  const isGuncel = (k: EnflasyonOrani) => k.tufeOrani != null && k.ufeOrani != null;

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Enflasyon Oranları</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>TÜFE ve Yİ-ÜFE aylık oranları — kira artış hesaplamalarında referans alınır</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setShowAddModal(true)}
            style={btnOrange}
          >
            + Manuel Ekle
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={card}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, display: 'block', marginBottom: 10 }} />
            Yükleniyor...
          </div>
        ) : kayitlar.length === 0 ? (
          <div style={{ padding: 56, textAlign: 'center', color: '#9ca3af' }}>
            <i className="fas fa-chart-line" style={{ fontSize: 32, marginBottom: 12, display: 'block', opacity: 0.4 }} />
            <div style={{ fontSize: 14, fontWeight: 500 }}>Henüz veri yok</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>TCMB'den çekerek veya manuel giriş yaparak başlayın</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Dönem</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>TÜFE<br /><span style={{ fontWeight: 400, fontSize: 10, textTransform: 'none' }}>12 Ayl. Ort.</span></th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Yİ-ÜFE<br /><span style={{ fontWeight: 400, fontSize: 10, textTransform: 'none' }}>12 Ayl. Ort.</span></th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Durum</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Kaynak</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Son Güncelleme</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {kayitlar.map((k, idx) => (
                <tr
                  key={k.enflasyonID}
                  style={{ ...rowHover, background: idx % 2 === 0 ? '#fff' : '#fafafa' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#fef3e8')}
                  onMouseLeave={e => (e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa')}
                >
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 600, color: '#1f2937' }}>
                      {AY_ADLARI[k.ay]} {k.yil}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {k.tufeOrani != null
                      ? <span style={{ fontWeight: 700, color: '#b45309', fontSize: 14 }}>{formatOran(k.tufeOrani)}</span>
                      : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {k.ufeOrani != null
                      ? <span style={{ fontWeight: 700, color: '#1d4ed8', fontSize: 14 }}>{formatOran(k.ufeOrani)}</span>
                      : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {isGuncel(k) ? guncelBadge : bekleyenBadge}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    {k.isAuto
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#0369a1', background: '#e0f2fe', padding: '3px 8px', borderRadius: 12 }}>
                          <i className="fas fa-robot" style={{ fontSize: 10 }} /> TCMB API
                        </span>
                      : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#6d28d9', background: '#ede9fe', padding: '3px 8px', borderRadius: 12 }}>
                          <i className="fas fa-user-edit" style={{ fontSize: 10 }} /> Manuel
                        </span>
                    }
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
                    {formatTarih(k.guncellemeTarihi)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                      <button
                        onClick={() => openEdit(k)}
                        title="Düzenle"
                        style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #e5e7eb', background: '#f9fafb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.color = '#cd6906'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.color = '#374151'; }}
                      >
                        <i className="fas fa-pen" style={{ fontSize: 11 }} />
                      </button>
                      <button
                        onClick={() => handleDelete(k)}
                        title="Sil"
                        style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #fecaca', background: '#fff5f5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fff5f5'; }}
                      >
                        <i className="fas fa-trash" style={{ fontSize: 11 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Modal */}
      {editTarget && (
        <div
          onClick={() => setEditTarget(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
              Oran Düzenle
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 20 }}>
              {AY_ADLARI[editTarget.ay]} {editTarget.yil}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
              <div>
                <label style={lbl}>TÜFE Oranı (%)</label>
                <input
                  type="text"
                  placeholder="Örn: 65,24"
                  value={editTufe}
                  onChange={e => setEditTufe(e.target.value)}
                  style={inp}
                  autoFocus
                />
              </div>
              <div>
                <label style={lbl}>Yİ-ÜFE Oranı (%)</label>
                <input
                  type="text"
                  placeholder="Örn: 46,25"
                  value={editUfe}
                  onChange={e => setEditUfe(e.target.value)}
                  style={inp}
                />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setEditTarget(null)} style={btnGhost}>İptal</button>
              <button
                onClick={handleEditSave}
                disabled={editSaving}
                style={{ ...btnOrange, background: editSaving ? '#e5e7eb' : '#cd6906', color: editSaving ? '#9ca3af' : '#fff', cursor: editSaving ? 'not-allowed' : 'pointer' }}
              >
                {editSaving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manuel Ekle Modal */}
      {showAddModal && (
        <div
          onClick={() => setShowAddModal(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, padding: 28, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 16 }}>Manuel Veri Girişi</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Yıl *</label>
                <input type="number" min="2000" max="2100" value={addYil} onChange={e => setAddYil(e.target.value)} style={inp} />
              </div>
              <div>
                <label style={lbl}>Ay *</label>
                <select value={addAy} onChange={e => setAddAy(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                  {AY_ADLARI.slice(1).map((ad, i) => (
                    <option key={i + 1} value={i + 1}>{ad}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
              <div>
                <label style={lbl}>TÜFE Oranı (%)</label>
                <input type="text" placeholder="Örn: 65,24" value={addTufe} onChange={e => setAddTufe(e.target.value)} style={inp} />
              </div>
              <div>
                <label style={lbl}>Yİ-ÜFE Oranı (%)</label>
                <input type="text" placeholder="Örn: 46,25" value={addUfe} onChange={e => setAddUfe(e.target.value)} style={inp} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowAddModal(false)} style={btnGhost}>İptal</button>
              <button
                onClick={handleAdd}
                disabled={addSaving}
                style={{ ...btnOrange, background: addSaving ? '#e5e7eb' : '#cd6906', color: addSaving ? '#9ca3af' : '#fff', cursor: addSaving ? 'not-allowed' : 'pointer' }}
              >
                {addSaving ? 'Kaydediliyor...' : 'Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default EnflasyonOranlari;
